import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ModalDisplayRequest } from '@iupics-components/models/ui-request';
import { ViewType } from '@iupics-components/models/view-type.enum';
import ProcessUiComponent from '@iupics-components/specific/window/process-ui/process-ui.component';
import { FavoriteSelectorComponent } from '@iupics-components/standard/grid/favorite-selector/favorite-selector.component';
import { ViewColumnsService } from '@iupics-components/standard/grid/services/view-columns.service';
import ModalUiComponent from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import { Global } from '@iupics-manager/models/global-var';
import { ToolbarProcess } from '@iupics-manager/models/toolbar-item';
import { ContentProjectionDirective, ToolbarComponent } from '@iupics/apiz-grid';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { has, isNil } from 'lodash';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { KeybindingDirective } from '../../../directives/keybinding.directive';
import UniversalFilterUiComponent from '../universal-filter-ui/universal-filter-ui.component';

@Component({
  selector: 'iu-menu-bar-ui',
  templateUrl: './menu-bar-ui.component.html',
  styleUrls: ['./menu-bar-ui.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    ToolbarModule,
    NgStyle,
    NgClass,
    ButtonModule,
    KeybindingDirective,
    TooltipModule,
    UniversalFilterUiComponent,
    TranslateModule,
    NgTemplateOutlet,
    //#region Apiz-grid
    ToolbarComponent,
    ContentProjectionDirective,
    FavoriteSelectorComponent,
    //#endregion
    forwardRef(() => ModalUiComponent),
    ProcessUiComponent,
  ],
})
export default class MenuBarUiComponent implements OnInit, OnDestroy {
  #translate = inject(TranslateService);
  #viewColumnService = inject(ViewColumnsService);

  @Input() isFromForm = false;
  @Input() activeTabID: string;
  @Input() gridView: any;
  /**
   * Sers à cacher le menu-bar detail pour les forms
   */
  @Input() hasUniversalFilter = true;
  @Input() isDeleteable = true;
  @Input() isInsertRecord = true;
  @Input() isReadOnly = false;
  @Input() isSplitView = false;
  @Input() isTabTopLevel = true;
  @Input() tabId: number;
  @Input() visibleButton = true;
  @Input() visibleTreeButton: boolean;

  @Output() readonly changeViewEvent = new EventEmitter();
  @Output() readonly filterChange = new EventEmitter<boolean>();
  @Output() readonly newEmitter = new EventEmitter();
  @Output() readonly openExportDataModalEmitter = new EventEmitter();
  @Output() readonly refreshEmitter = new EventEmitter();
  @Output() readonly removeEmitter = new EventEmitter<MouseEvent>();
  newBtnCondition = false;
  visibleButtonMultiSelection = false;
  tooltipLabels: any;
  isMobile = Global.isMobile();
  showMoreAction = false;

  get isRecordActive(): boolean {
    let response = true;
    if (
      has(this.gridView?.DOMParentComponent?.dataStored?.data, 'IsActive') &&
      !isNil(this.gridView.DOMParentComponent.dataStored.data.IsActive)
    ) {
      response = this.gridView.DOMParentComponent.dataStored.data.IsActive === 'Y';
    }

    return response;
  }

  processes = this.#viewColumnService.processes;

  ngOnInit(): void {
    this.tooltipLabels = {
      add: this.#translate.instant('menuBarUi.add'),
      delete: this.#translate.instant('menuBarUi.delete'),
      refresh: this.#translate.instant('menuBarUi.refresh'),
      export: this.#translate.instant('menuBarUi.export'),
      gridView: this.#translate.instant('menuBarUi.gridView'),
      kanbanView: this.#translate.instant('menuBarUi.kanbanView'),
      calendarView: this.#translate.instant('menuBarUi.calendarView'),
      chartView: this.#translate.instant('menuBarUi.chartView'),
      treeView: this.#translate.instant('menuBarUi.treeView'),
      filter: this.#translate.instant('menuBarUi.filter'),
      search: this.#translate.instant('menuBarUi.search'),
    };
    this.newBtnCondition = !this.isReadOnly && this.isInsertRecord && this.isRecordActive;
  }

  ngOnDestroy(): void {
    for (const key of Object.keys(this)) {
      if (this[key] && this[key] instanceof EventEmitter) {
        this[key].complete();
        this[key] = undefined;
      }
    }
  }

  onShowMoreAction(event): void {
    event.stopPropagation();
    this.showMoreAction = !this.showMoreAction;
  }

  setVisibleButton(visible: boolean): void {
    this.visibleButton = visible;
  }

  setVisibleButtonMultiSelection(visible: boolean): void {
    this.visibleButtonMultiSelection = visible;
  }

  switchToGridView(): void {
    this.changeViewEvent.emit(ViewType.GRID);
  }

  switchToKanbanView(): void {
    this.changeViewEvent.emit(ViewType.KANBAN);
  }

  switchToCalendarView(): void {
    this.changeViewEvent.emit(ViewType.CALENDAR);
  }

  switchToChartView(): void {
    this.changeViewEvent.emit(ViewType.CHART);
  }

  switchToTreeView(): void {
    this.changeViewEvent.emit(ViewType.TREE);
  }

  updateButtonLists(): void {
    this.newBtnCondition = !this.isReadOnly && this.isInsertRecord && this.isRecordActive;
  }

  showProcessUI(process: ToolbarProcess): void {
    this.#viewColumnService.updateModalDisplay.emit(
      ModalDisplayRequest.Process(true, process.processId, { from: 'multiProcess' })
    );
  }
}
